'use client';

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  DEFAULT_LANG,
  type Dictionary,
  type Lang,
  isTranslatable,
  isTranslatablePath,
  langFromPath,
  normalise,
  pathForLang,
} from '@/lib/i18n';
import { loadDictionary } from '@/lib/translate/dictionary';
import { fetchOverrides } from '@/lib/translations';

/**
 * Translates the page as it is rendered, rather than asking every string to
 * announce itself.
 *
 * The words come from the dictionary shipped in the bundle
 * (src/lib/translate/dictionaries), so a Lithuanian page needs nothing at
 * runtime — no translation service, no key, no quota, no request that can fail.
 * Anything corrected in the dashboard is layered on top when it loads.
 *
 * The alternative was wrapping some five hundred strings across seventy files
 * in a helper. That is more precise, and it is also a trap: the day someone
 * adds a section, edits a service description or publishes an article from the
 * dashboard, the new wording is not wrapped and silently stays English. Nobody
 * finds out until a Lithuanian visitor does. Working on the rendered text
 * instead means new content is covered the moment it appears, which is the
 * whole requirement.
 *
 * What it will not touch:
 *  - anything inside `[translate="no"]`, `[data-no-translate]`, `<code>`,
 *    `<pre>`, `<script>`, `<style>` or a form field
 *  - the dashboard, which is English whatever the URL says
 *  - anything that is not words: numbers, prices, dates, URLs, addresses
 *
 * Text is written back with `nodeValue`, never `innerHTML`. A translation is
 * data from a database and is treated as text, so a poisoned row could at worst
 * put wrong words on the page — never markup, never script.
 */

interface TranslationContextValue {
  lang: Lang;
  translate: (text: string) => string;
}

const TranslationContext = createContext<TranslationContextValue>({
  lang: 'en',
  translate: (text) => text,
});

export function useTranslation() {
  return useContext(TranslationContext);
}

const SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'CODE',
  'PRE',
  'KBD',
  'SAMP',
  'TEXTAREA',
  'INPUT',
  'SELECT',
  'OPTION',
]);

/** Attributes whose value is read aloud or shown, so they need translating too. */
const TEXT_ATTRIBUTES = ['aria-label', 'placeholder', 'title', 'alt'];

export default function TranslationProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || '/';
  const router = useRouter();
  /* The URL is the source of truth for the language — that is what makes a
     Lithuanian page shareable — and the dashboard is never translated whatever
     the URL says. */
  const lang: Lang = isTranslatablePath(pathname) ? langFromPath(pathname) : 'en';
  const [dictionary, setDictionary] = useState<Dictionary>({});

  /* One map, seeded from the table and from the last visit's cache, and added
     to as machine translations come back. Held in a ref because the DOM pass
     reads it on every mutation and must not re-render anything to do so. */
  const dict = useRef<Dictionary>({});
  const root = useRef<HTMLDivElement | null>(null);

  /* Every text node and attribute this has rewritten, with the English it
     replaced.
   *
   * Needed because the translation is a direct write to the DOM, which React
   * knows nothing about: as far as React is concerned those nodes still hold
   * the English it rendered, so switching back to English re-renders nothing
   * and the page stays Lithuanian. The originals have to be put back by hand.
   *
   * It also gives every rewritten node a stable key. A second pass — when a
   * correction from the dashboard arrives — would otherwise look the node up by
   * its current text, which is already Lithuanian, find nothing, and leave the
   * correction unapplied until the next reload. */
  const rewritten = useRef(new Map<Text, { english: string; written: string }>());
  const rewrittenAttrs = useRef(
    new Map<Element, Map<string, { english: string; written: string }>>()
  );
  /* Links whose destination was moved into this language. */
  const rewrittenHrefs = useRef(new Map<Element, { english: string; written: string }>());
  /* Sentences reassembled from several nodes split by <br>. Restoring one means
     putting every fragment back, not just the first. */
  const rewrittenJoined = useRef(
    new Map<Element, { english: string; written: string; nodes: Text[]; originals: string[] }>()
  );
  /** Which language the rewrites above belong to. */
  const rewrittenLang = useRef<Lang>('en');

  const value = useMemo<TranslationContextValue>(
    () => ({
      lang,
      translate: (text: string) => dict.current[normalise(text)] ?? text,
    }),
    [lang]
  );

  /* The bundled dictionary first, because it is already here and needs no
     network at all — then whatever a person has corrected in the dashboard,
     layered over it. The corrections are an enhancement, not a dependency: if
     the database is unreachable the page is still fully translated. */
  useEffect(() => {
    if (lang === 'en') return;
    let cancelled = false;

    void (async () => {
      const bundled = await loadDictionary(lang);
      if (cancelled) return;
      if (Object.keys(bundled).length) setDictionary(bundled);

      const overrides = await fetchOverrides(lang);
      if (cancelled || !Object.keys(overrides).length) return;
      setDictionary({ ...bundled, ...overrides });
    })();

    return () => {
      cancelled = true;
    };
  }, [lang]);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const container = root.current;
    if (!container) return;

    /* Undo the previous language before doing anything else. Only a change of
       language undoes anything: a second pass in the same language (the
       dashboard's corrections arriving) improves the text in place rather than
       flashing English in between. */
    if (rewrittenLang.current !== lang) {
      for (const [node, { english, written }] of rewritten.current) {
        // Only if it still holds what we wrote. Anything else is React's, and
        // putting our copy of the English back over it would be a regression.
        if (node.isConnected && node.nodeValue === written) node.nodeValue = english;
      }
      rewritten.current.clear();

      for (const [element, attrs] of rewrittenAttrs.current) {
        for (const [name, { english, written }] of attrs) {
          if (element.isConnected && element.getAttribute(name) === written) {
            element.setAttribute(name, english);
          }
        }
      }
      rewrittenAttrs.current.clear();

      for (const [element, { nodes, originals, written }] of rewrittenJoined.current) {
        if (!element.isConnected || nodes[0]?.nodeValue !== written) continue;
        nodes.forEach((node, i) => {
          node.nodeValue = originals[i];
        });
        element.removeAttribute('data-i18n-joined');
      }
      rewrittenJoined.current.clear();

      for (const [anchor, { english, written }] of rewrittenHrefs.current) {
        if (anchor.isConnected && anchor.getAttribute('href') === written) {
          anchor.setAttribute('href', english);
        }
      }
      rewrittenHrefs.current.clear();

      rewrittenLang.current = lang;
    }

    if (lang === 'en') return;

    dict.current = dictionary;

    const skipped = (element: Element | null): boolean => {
      for (let el = element; el; el = el.parentElement) {
        if (SKIP_TAGS.has(el.tagName)) return true;
        if (el.getAttribute('translate') === 'no') return true;
        if (el.hasAttribute('data-no-translate')) return true;
      }
      return false;
    };

    /* A string the dictionary does not have stays English. That is the whole
       failure mode of this design, and it is a mild one: the page reads
       correctly, in the other language, until someone adds the sentence to the
       dictionary. `npm run check:translations` lists them. */
    const missing = new Set<string>();
    const note = (text: string) => {
      if (missing.has(text)) return;
      missing.add(text);
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.info(`[i18n] no ${lang} translation for: ${text}`);
      }
    };

    const translateNode = (node: Text) => {
      const record = rewritten.current.get(node);
      /* A node this has already rewritten is looked up by the English it used
         to hold, not by the Lithuanian now in it — otherwise a correction
         arriving a moment later would find nothing to correct. */
      const original =
        record && node.nodeValue === record.written ? record.english : (node.nodeValue ?? '');
      const source = normalise(original);
      if (!isTranslatable(source)) return;
      const hit = dict.current[source];
      if (!hit || hit === source) {
        note(source);
        return;
      }
      /* Keep the surrounding whitespace: JSX puts meaningful single spaces
         between inline elements, and eating them runs words together. */
      const lead = original.match(/^\s*/)?.[0] ?? '';
      const tail = original.match(/\s*$/)?.[0] ?? '';
      const next = `${lead}${hit}${tail}`;
      if (node.nodeValue === next) return;
      node.nodeValue = next;
      rewritten.current.set(node, { english: original, written: next });
    };

    /* Links.
     *
     * Every internal link on this site is written as its English path —
     * `/services`, `/insights`, `/#contact`. Followed as-is from a Lithuanian
     * page they land on the English one, which is how a visitor who switched
     * language found themselves back in English on the very next click.
     *
     * So the destination is moved into the current language, in two places that
     * both have to be right:
     *
     *  - the href itself, which is what a middle-click, a "copy link address"
     *    and a right-click "open in new tab" use;
     *  - the click, because Next's <Link> navigates to the href it was given as
     *    a prop and never looks at the attribute we just changed.
     */
    const localised = (raw: string | null): string | null => {
      if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
      const hashAt = raw.indexOf('#');
      const hash = hashAt === -1 ? '' : raw.slice(hashAt);
      const withoutHash = hashAt === -1 ? raw : raw.slice(0, hashAt);
      const queryAt = withoutHash.indexOf('?');
      const query = queryAt === -1 ? '' : withoutHash.slice(queryAt);
      const path = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
      // A file, not a page: /assets/x.png, /sitemap.xml.
      if (/\.[a-z0-9]+$/i.test(path)) return null;
      // The dashboard and the machinery have no translated twin.
      if (!isTranslatablePath(path)) return null;
      // Already pointed at a language.
      if (langFromPath(path) !== DEFAULT_LANG) return null;
      return `${pathForLang(path, lang)}${query}${hash}`;
    };

    const translateLink = (element: Element) => {
      if (element.tagName !== 'A') return;
      /* A link that declares its own language means it — the EN/LT switch is
         the one control on the page whose whole job is to leave this one. */
      if (element.hasAttribute('hreflang')) return;

      const record = rewrittenHrefs.current.get(element);
      const now = element.getAttribute('href');
      const english = record && now === record.written ? record.english : now;
      const next = localised(english);
      if (!next || now === next) return;
      element.setAttribute('href', next);
      rewrittenHrefs.current.set(element, { english: english as string, written: next });
    };

    const translateAttributes = (element: Element) => {
      for (const name of TEXT_ATTRIBUTES) {
        const now = element.getAttribute(name);
        if (!now) continue;
        const record = rewrittenAttrs.current.get(element)?.get(name);
        const original = record && now === record.written ? record.english : now;
        const source = normalise(original);
        if (!isTranslatable(source)) continue;
        const hit = dict.current[source];
        if (!hit || hit === source) {
          note(source);
          continue;
        }
        if (now === hit) continue;
        element.setAttribute(name, hit);
        const attrs = rewrittenAttrs.current.get(element) ?? new Map();
        attrs.set(name, { english: original, written: hit });
        rewrittenAttrs.current.set(element, attrs);
      }
    };

    /* A sentence broken across lines by <br> is several text nodes, and
       translating them one at a time turns "Everything your / business needs /
       to grow online" into three unrelated fragments — wrong on its own terms,
       and unfixable once a machine translator has seen them separately.

       So where an element holds nothing but text and line breaks, the sentence
       is the unit: translated whole, put back in the first node, the rest
       emptied, and the breaks hidden by CSS rather than removed, so nothing
       React owns leaves the DOM. Those breaks were a typographic decision taken
       for the English wording; Lithuanian is a different length and wraps where
       it needs to. */
    const joinable = (el: Element): boolean => {
      let texts = 0;
      let breaks = 0;
      for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
          if (normalise(child.nodeValue ?? '')) texts += 1;
        } else if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'BR') {
          breaks += 1;
        } else {
          return false;
        }
      }
      return breaks > 0 && texts > 1;
    };

    const translateJoined = (el: Element) => {
      const record = rewrittenJoined.current.get(el);
      /* Once joined, the fragments are gone from the DOM — the sentence lives
         in the first node and the rest are empty. So a second pass reads the
         English off the record rather than off the page. */
      const nodes =
        record && record.nodes[0]?.nodeValue === record.written
          ? record.nodes
          : Array.from(el.childNodes).filter(
              (c): c is Text =>
                c.nodeType === Node.TEXT_NODE && Boolean(normalise(c.nodeValue ?? ''))
            );
      if (!nodes.length) return;

      const originals = record ? record.originals : nodes.map((n) => n.nodeValue ?? '');
      const source = record ? record.english : normalise(originals.join(' '));
      if (!isTranslatable(source)) return;
      const hit = dict.current[source];
      if (!hit || hit === source) {
        note(source);
        return;
      }
      if (nodes[0].nodeValue === hit) return;
      nodes[0].nodeValue = hit;
      for (const node of nodes.slice(1)) if (node.nodeValue !== '') node.nodeValue = '';
      el.setAttribute('data-i18n-joined', 'true');
      rewrittenJoined.current.set(el, { english: source, written: hit, nodes, originals });
    };

    const walk = (from: Node) => {
      if (from.nodeType === Node.TEXT_NODE) {
        if (!skipped(from.parentElement)) translateNode(from as Text);
        return;
      }
      if (from.nodeType !== Node.ELEMENT_NODE) return;
      const el = from as Element;
      if (
        SKIP_TAGS.has(el.tagName) ||
        el.getAttribute('translate') === 'no' ||
        el.hasAttribute('data-no-translate')
      ) {
        return;
      }
      translateAttributes(el);
      translateLink(el);
      if (joinable(el) || rewrittenJoined.current.has(el)) {
        translateJoined(el);
        return;
      }
      for (const child of Array.from(el.childNodes)) walk(child);
    };

    walk(container);

    /* React re-renders, route changes and a card opening its deliverables all
       add text after the first pass. Watching is what keeps this true for the
       life of the page rather than for its first frame. */
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'characterData') {
          if (!skipped(record.target.parentElement)) translateNode(record.target as Text);
        } else if (record.type === 'attributes') {
          if (record.target.nodeType === Node.ELEMENT_NODE) {
            const el = record.target as Element;
            if (!skipped(el)) {
              translateAttributes(el);
              translateLink(el);
            }
          }
        } else {
          record.addedNodes.forEach((node) => walk(node));
        }
      }
    });
    observer.observe(container, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...TEXT_ATTRIBUTES, 'href'],
    });

    /* Capture phase: React attaches its listeners at the root in the bubble
       phase, so this sees the click first and can stop <Link> from pushing the
       English path it was rendered with. Only links this pass actually moved
       are intercepted, so an English page behaves exactly as it always did. */
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.('a[href]');
      if (!anchor) return;
      if (anchor.getAttribute('target') === '_blank' || anchor.hasAttribute('download')) return;
      const record = rewrittenHrefs.current.get(anchor);
      if (!record || anchor.getAttribute('href') !== record.written) return;
      event.preventDefault();
      event.stopPropagation();
      router.push(record.written);
    };
    document.addEventListener('click', onClick, true);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', onClick, true);
    };
  }, [lang, dictionary, router]);

  return (
    <TranslationContext.Provider value={value}>
      <div ref={root} data-lang={lang} style={{ display: 'contents' }}>
        {children}
      </div>
    </TranslationContext.Provider>
  );
}
